/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEnvelope, faEnvelopeOpen, faHome, faArrowLeft } from "@fortawesome/free-solid-svg-icons";
import getSupabaseClient from "../../../utils/supabase-client";
import "./messages.css";

function Messages() {
  const [conversations, setConversations] = useState([]);
  const [activeRentals, setActiveRentals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [sendingMessage, setSendingMessage] = useState(false);
  const [isOtherUserTyping, setIsOtherUserTyping] = useState(false);
  const selectedConversationRef = useRef(null);
  const realtimeChannelRef = useRef(null);
  const typingChannelRef = useRef(null);
  const typingTimeoutRef = useRef(null);
  const isFetchingRef = useRef(false);
  const navigate = useNavigate();

  const getUserIdFromToken = () => {
    const token = localStorage.getItem("token");
    if (!token) return null;

    try {
      const payload = token.split(".")[1];
      const parsedPayload = JSON.parse(atob(payload));
      return Number(parsedPayload.id) || null;
    } catch (error) {
      console.warn("Unable to decode token for realtime messaging", error);
      return null;
    }
  };

  const doesEventMatchSelectedConversation = (eventRow) => {
    const selected = selectedConversationRef.current;
    if (!selected || !eventRow) return false;

    const sameProperty = Number(eventRow.property_id) === Number(selected.property_id);
    const participants = [Number(eventRow.sender_id), Number(eventRow.receiver_id)];
    const sameOtherUser = participants.includes(Number(selected.other_user_id));

    return sameProperty && sameOtherUser;
  };

  // Intentionally initialize channels once on mount.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    fetchConversations();
    fetchActiveRentals();

    const supabase = getSupabaseClient();
    const userId = getUserIdFromToken();

    if (!supabase || !userId) {
      return;
    }

    const channel = supabase
      .channel(`inquiries-realtime-${userId}`)
      .on(
        "postgres_changes",
        {
          event: "INSERT",
          schema: "public",
          table: "inquiries",
          filter: `receiver_id=eq.${userId}`,
        },
        async (payload) => {
          await fetchConversations();
          if (doesEventMatchSelectedConversation(payload.new)) {
            await fetchConversationMessages(
              payload.new.property_id,
              selectedConversationRef.current?.other_user_id
            );
          }
        }
      )
      .on(
        "postgres_changes",
        {
          event: "UPDATE",
          schema: "public",
          table: "inquiries",
          filter: `receiver_id=eq.${userId}`,
        },
        async (payload) => {
          if (payload.new?.is_read !== payload.old?.is_read) {
            await fetchConversations();
          }
        }
      )
      .subscribe();

    realtimeChannelRef.current = channel;

    return () => {
      if (realtimeChannelRef.current) {
        supabase.removeChannel(realtimeChannelRef.current);
      }
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    selectedConversationRef.current = selectedConversation;
  }, [selectedConversation]);

  // Poll for new messages every 3 seconds when a conversation is open
  useEffect(() => {
    if (!selectedConversation) return;

    const propertyId = selectedConversation.property_id || selectedConversation.propertyId;
    const otherUserId = selectedConversation.other_user_id || selectedConversation.contactUserId;
    if (!propertyId || !otherUserId) return;

      // eslint-disable-next-line react-hooks/exhaustive-deps
    const interval = setInterval(async () => {
      if (isFetchingRef.current) return;
      isFetchingRef.current = true;
      try {
        await fetchMessages(propertyId, otherUserId);
      } finally {
        isFetchingRef.current = false;
      }
    }, 3000);

    return () => clearInterval(interval);
  }, [selectedConversation]); // eslint-disable-line react-hooks/exhaustive-deps

  // Typing indicator via Supabase Presence
  useEffect(() => {
    const supabase = getSupabaseClient();
    const userId = getUserIdFromToken();

    if (!supabase || !userId || !selectedConversation) return;

    const propertyId = selectedConversation.property_id || selectedConversation.propertyId;
    const otherUserId = selectedConversation.other_user_id || selectedConversation.contactUserId;
    if (!propertyId || !otherUserId) return;

    const channelName = `typing-${propertyId}-${[userId, otherUserId].sort().join("-")}`;
    const channel = supabase.channel(channelName);

    channel
      .on("presence", { event: "sync" }, () => {
        const state = channel.presenceState();
        const others = Object.values(state).flat().filter((p) => p.user_id !== userId);
        setIsOtherUserTyping(others.some((p) => p.is_typing));
      })
      .subscribe();

    typingChannelRef.current = channel;

    return () => {
      setIsOtherUserTyping(false);
      if (typingChannelRef.current) {
        supabase.removeChannel(typingChannelRef.current);
        typingChannelRef.current = null;
      }
    };
  }, [selectedConversation]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchConversations = async () => {
    const token = localStorage.getItem("token");
    if (!token) {
      navigate("/login");
      return;
    }

    try {
      const response = await fetch("/api/inquiries/conversations", {
        headers: {
          "Authorization": `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setConversations(data);
      } else {
        console.error("Failed to fetch conversations");
      }
    } catch (error) {
      console.error("Error fetching conversations:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchActiveRentals = async () => {
    const token = localStorage.getItem("token");
    const userRole = localStorage.getItem("role");
    
    if (!token) return;

    try {
      // Fetch rentals based on user role
      const endpoint = userRole === "renter" 
        ? "/api/renter/rentals"
        : "/api/owner/properties-with-rentals";
        
      const response = await fetch(endpoint, {
        headers: {
          "Authorization": `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        
        if (userRole === "renter") {
          // For renters, show active rentals with landlord info
          const activeRents = data.filter(r => r.status === 'active').map(rental => ({
            propertyId: rental.propertyId,
            propertyName: rental.propertyName,
            contactUserId: rental.ownerId,
            contactName: rental.landlord || "Property Owner",
            contactRole: "Owner"
          }));
          setActiveRentals(activeRents);
        } else {
          // For owners, show properties with active rentals and renter info
          const propertiesWithRentals = data
            .filter(p => p.rentalInfo && p.rentalInfo.status === 'active')
            .map(property => ({
              propertyId: property.property_id,
              propertyName: property.property_name,
              contactUserId: property.rentalInfo.renterId,
              contactName: property.rentalInfo.renterName || "Renter",
              contactRole: "Renter"
            }));
          setActiveRentals(propertiesWithRentals);
        }
      }
    } catch (error) {
      console.error("Error fetching active rentals:", error);
    }
  };

  // Fetches messages only (used by polling — no markAsRead side-effect)
  const fetchMessages = async (propertyId, otherUserId) => {
    const token = localStorage.getItem("token");
    try {
      const response = await fetch(
        `/api/inquiries/property/${propertyId}?otherUserId=${otherUserId}`,
        { headers: { "Authorization": `Bearer ${token}` } }
      );
      if (response.ok) {
        const data = await response.json();
        setMessages(data);
      }
    } catch (error) {
      console.error("Error fetching messages:", error);
    }
  };

  // Fetches messages AND marks them as read (used when opening a conversation)
  const fetchConversationMessages = async (propertyId, otherUserId) => {
    await fetchMessages(propertyId, otherUserId);
    await markAsRead(propertyId, otherUserId);
  };

  const markAsRead = async (propertyId, otherUserId) => {
    const token = localStorage.getItem("token");

    try {
      await fetch(`/api/inquiries/mark-read/${propertyId}/${otherUserId}`, {
        method: "PATCH",
        headers: {
          "Authorization": `Bearer ${token}`
        }
      });
      
      // Refresh conversations to update unread count
      fetchConversations();
    } catch (error) {
      console.error("Error marking as read:", error);
    }
  };

  const handleConversationClick = (conversation) => {
    setSelectedConversation(conversation);
    fetchConversationMessages(conversation.property_id, conversation.other_user_id);
  };

  const handleQuickMessageClick = (rental) => {
    // Check if conversation already exists
    const existingConv = conversations.find(
      c => c.property_id === rental.propertyId && c.other_user_id === rental.contactUserId
    );

    if (existingConv) {
      handleConversationClick(existingConv);
    } else {
      // Create new conversation object
      const newConversation = {
        property_id: rental.propertyId,
        property_name: rental.propertyName,
        other_user_id: rental.contactUserId,
        other_user_name: rental.contactName,
        last_message: "Start a conversation",
        last_message_time: new Date().toISOString(),
        unread_count: 0
      };
      setSelectedConversation(newConversation);
      setMessages([]);
    }
  };

  const handleTyping = () => {
    const supabase = getSupabaseClient();
    if (!supabase || !typingChannelRef.current) return;

    const userId = getUserIdFromToken();
    typingChannelRef.current.track({ user_id: userId, is_typing: true });

    clearTimeout(typingTimeoutRef.current);
    typingTimeoutRef.current = setTimeout(() => {
      typingChannelRef.current?.track({ user_id: userId, is_typing: false });
    }, 2000);
  };

  const handleSendMessage = async () => {
    if (!newMessage.trim() || !selectedConversation) return;

    setSendingMessage(true);
    const token = localStorage.getItem("token");
    const propertyId = selectedConversation.property_id || selectedConversation.propertyId;
    const receiverId = selectedConversation.other_user_id || selectedConversation.contactUserId;

    if (!propertyId || !receiverId) {
      alert("Conversation data is incomplete. Please reopen the chat and try again.");
      setSendingMessage(false);
      return;
    }

    try {
      const response = await fetch("/api/inquiries", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`
        },
        body: JSON.stringify({
          propertyId,
          receiverId,
          message: newMessage
        })
      });

      if (response.ok) {
        setNewMessage("");
        // Stop typing indicator immediately on send
        if (typingChannelRef.current) {
          const userId = getUserIdFromToken();
          typingChannelRef.current.track({ user_id: userId, is_typing: false });
        }
        clearTimeout(typingTimeoutRef.current);
        // Refresh messages
        fetchConversationMessages(propertyId, receiverId);
        fetchConversations();
      } else {
        let errorMessage = "Failed to send message";
        try {
          const errorData = await response.json();
          if (errorData?.message) errorMessage = errorData.message;
        } catch (e) {
          // Keep default message if response has no JSON body.
        }
        alert(errorMessage);
      }
    } catch (error) {
      console.error("Error sending message:", error);
      alert("Error connecting to messaging server");
    } finally {
      setSendingMessage(false);
    }
  };

  const handleBackToList = () => {
    setSelectedConversation(null);
    setMessages([]);
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return "Just now";
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    
    return date.toLocaleDateString();
  };

  if (loading) {
    return (
      <div className="container mt-5 text-center">
        <div className="spinner-border" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="messages-container">
      <div className="container py-4">
        <div className="row">
          <div className="col-12">
            <h2 className="mb-4">
              <FontAwesomeIcon icon={faEnvelope} className="me-2" />
              Messages
            </h2>
          </div>
        </div>

        <div className="row">
          {/* Conversations List */}
          <div className={`col-md-4 ${selectedConversation ? 'd-none d-md-block' : ''}`}>
            {/* Quick Message Section for Active Rentals */}
            {activeRentals.length > 0 && (
              <div className="card mb-3">
                <div className="card-header bg-success text-white">
                  <h6 className="mb-0">Active Rentals - Quick Message</h6>
                </div>
                <div className="card-body p-0">
                  <div className="quick-message-list">
                    {activeRentals.map((rental, index) => (
                      <div
                        key={index}
                        className="conversation-item quick-message-item"
                        onClick={() => handleQuickMessageClick(rental)}
                        style={{ cursor: 'pointer' }}
                      >
                        <div className="d-flex align-items-center">
                          <div className="flex-shrink-0">
                            <div className="conversation-avatar bg-success">
                              <FontAwesomeIcon icon={faHome} />
                            </div>
                          </div>
                          <div className="flex-grow-1 ms-3">
                            <h6 className="mb-0 fw-semibold">{rental.propertyName}</h6>
                            <small className="text-muted">
                              Message {rental.contactRole}: {rental.contactName}
                            </small>
                          </div>
                          <div>
                            <FontAwesomeIcon icon={faEnvelope} className="text-success" />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            <div className="card">
              <div className="card-header bg-primary text-white">
                <h5 className="mb-0">All Conversations</h5>
              </div>
              <div className="card-body p-0">
                {conversations.length === 0 ? (
                  <div className="text-center p-4 text-muted">
                    <FontAwesomeIcon icon={faEnvelope} size="3x" className="mb-3" />
                    <p>No messages yet</p>
                    <button 
                      className="btn btn-primary"
                      onClick={() => navigate("/find-property")}
                    >
                      Find Properties
                    </button>
                  </div>
                ) : (
                  <div className="conversation-list">
                    {conversations.map((conv) => (
                      <div
                        key={`${conv.property_id}-${conv.other_user_id}`}
                        className={`conversation-item ${
                          selectedConversation?.property_id === conv.property_id &&
                          selectedConversation?.other_user_id === conv.other_user_id
                            ? "active"
                            : ""
                        }`}
                        onClick={() => handleConversationClick(conv)}
                      >
                        <div className="d-flex align-items-start">
                          <div className="flex-shrink-0">
                            <div className="conversation-avatar">
                              <FontAwesomeIcon icon={faHome} />
                            </div>
                          </div>
                          <div className="flex-grow-1 ms-3">
                            <div className="d-flex justify-content-between align-items-start">
                              <h6 className="mb-1 fw-semibold">{conv.property_name}</h6>
                              {conv.unread_count > 0 && (
                                <span className="badge bg-danger rounded-pill">
                                  {conv.unread_count}
                                </span>
                              )}
                            </div>
                            <p className="text-muted small mb-1">{conv.other_user_name}</p>
                            <p className="text-muted small mb-0 text-truncate">
                              {conv.last_message}
                            </p>
                            <small className="text-muted">
                              {formatDate(conv.last_message_time)}
                            </small>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Messages View */}
          <div className={`col-md-8 ${!selectedConversation ? 'd-none d-md-block' : ''}`}>
            {selectedConversation ? (
              <div className="card">
                <div className="card-header bg-primary text-white">
                  <div className="d-flex align-items-center">
                    <button
                      className="btn btn-sm btn-light me-2 d-md-none"
                      onClick={handleBackToList}
                    >
                      <FontAwesomeIcon icon={faArrowLeft} />
                    </button>
                    <div>
                      <h6 className="mb-0">{selectedConversation.property_name}</h6>
                      <small>{selectedConversation.other_user_name}</small>
                    </div>
                  </div>
                </div>
                <div className="card-body messages-body">
                  {messages.length === 0 ? (
                    <div className="text-center text-muted p-4">
                      <p>No messages yet. Start the conversation!</p>
                    </div>
                  ) : (
                    <div className="messages-thread">
                      {messages.map((msg) => (
                        <div
                          key={msg.id}
                          className={`message-bubble ${
                            msg.is_sent_by_me ? "sent" : "received"
                          }`}
                        >
                          <div className="message-content">
                            <p className="mb-1">{msg.message}</p>
                            <small className="text-muted">
                              {formatDate(msg.created_at)}
                            </small>
                          </div>
                        </div>
                      ))}
                      {isOtherUserTyping && (
                        <div className="message-bubble received">
                          <div className="message-content typing-indicator">
                            <span></span>
                            <span></span>
                            <span></span>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
                <div className="card-footer">
                  <div className="input-group">
                    <textarea
                      className="form-control"
                      rows="2"
                      placeholder="Type your message..."
                      value={newMessage}
                      onChange={(e) => { setNewMessage(e.target.value); handleTyping(); }}
                      onKeyPress={(e) => {
                        if (e.key === "Enter" && !e.shiftKey) {
                          e.preventDefault();
                          handleSendMessage();
                        }
                      }}
                    ></textarea>
                    <button
                      className="btn btn-primary"
                      onClick={handleSendMessage}
                      disabled={sendingMessage || !newMessage.trim()}
                    >
                      {sendingMessage ? (
                        <span className="spinner-border spinner-border-sm" role="status"></span>
                      ) : (
                        "Send"
                      )}
                    </button>
                  </div>
                  <small className="text-muted">Press Enter to send, Shift+Enter for new line</small>
                </div>
              </div>
            ) : (
              <div className="card">
                <div className="card-body text-center p-5 text-muted">
                  <FontAwesomeIcon icon={faEnvelopeOpen} size="4x" className="mb-3" />
                  <h5>Select a conversation</h5>
                  <p>Choose a conversation from the list to view messages</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default Messages;
